import React from 'react';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { EnhancedConfigParserPage } from '@/pages/EnhancedConfigParserPage';
import { EnhancedProviderManagerPage } from '@/pages/EnhancedProviderManagerPage';
import { EnhancedChatTestPage } from '@/pages/EnhancedChatTestPage';
import { HistoryManagerPage } from '@/pages/HistoryManagerPage';
import { useEnhancedAppStore } from '@/stores/EnhancedAppStore';
import { Zap, Database, Cpu, Server, Activity } from 'lucide-react';

// 页面配置
const PAGE_CONFIG = {
  config: {
    title: '智能配置识别',
    description: '自动识别并解析您的 API 配置信息',
    icon: <Zap className="w-5 h-5" />,
    color: 'primary',
    component: EnhancedConfigParserPage,
  },
  providers: {
    title: '供应商管理',
    description: '管理您的 API 供应商和配置',
    icon: <Database className="w-5 h-5" />,
    color: 'accent',
    component: EnhancedProviderManagerPage,
  },
  chat: {
    title: '智能对话测试',
    description: '测试 API 对话功能和性能',
    icon: <Cpu className="w-5 h-5" />,
    color: 'green',
    component: EnhancedChatTestPage,
  },
  history: {
    title: '历史记录管理',
    description: '查看和管理历史对话记录',
    icon: <Server className="w-5 h-5" />,
    color: 'purple',
    component: HistoryManagerPage,
  },
};

const COLOR_CLASSES: Record<string, {
  bg: string;
  text: string;
  border: string;
  progress: string;
}> = {
  primary: {
    bg: 'bg-primary-100',
    text: 'text-primary-800',
    border: 'border-primary-200',
    progress: 'bg-primary-500',
  },
  accent: {
    bg: 'bg-accent-100',
    text: 'text-accent-800',
    border: 'border-accent-200',
    progress: 'bg-accent-500',
  },
  green: {
    bg: 'bg-green-100',
    text: 'text-green-800',
    border: 'border-green-200',
    progress: 'bg-green-500',
  },
  purple: {
    bg: 'bg-purple-100',
    text: 'text-purple-800',
    border: 'border-purple-200',
    progress: 'bg-purple-500',
  },
};

export const EnhancedMainLayout: React.FC = () => {
  const { providers, sessions, currentProviderId, currentSessionId, providerStats } = useEnhancedAppStore();
  
  // 使用基础状态管理
  const [activeTab, setActiveTab] = React.useState<'config' | 'providers' | 'chat' | 'history'>('config');
  
  const currentProvider = providers.find(p => p.id === currentProviderId);
  const currentSession = sessions.find(s => s.id === currentSessionId);
  
  const pageConfig = PAGE_CONFIG[activeTab];
  const colorConfig = COLOR_CLASSES[pageConfig.color];
  const ContentComponent = pageConfig.component;

  // 计算统计信息
  const stats = React.useMemo(() => {
    const totalProviders = providers.length;
    const onlineProviders = providers.filter(p => {
      const stats = providerStats[p.id];
      return stats?.lastSuccess && (stats.successCount / stats.testCount) >= 0.8;
    }).length;
    
    const totalSessions = sessions.length;
    const activeSessions = sessions.filter(s => 
      new Date(s.lastMessageAt).getTime() > Date.now() - 24 * 60 * 60 * 1000
    ).length;
    
    return {
      totalProviders,
      onlineProviders,
      totalSessions,
      activeSessions,
    };
  }, [providers, sessions, providerStats]);

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* 顶部导航栏 */}
      <Header />

      <div className="flex flex-1 overflow-hidden">
        {/* 左侧导航栏 - 使用原版但添加点击事件 */}
        <div className="w-64 border-r border-gray-200 bg-white overflow-y-auto">
          <Sidebar />
        </div>

        {/* 主内容区 */}
        <main className="flex-1 overflow-auto p-6">
          <div className="max-w-7xl mx-auto">
            {/* 头部信息栏 */}
            <div className="mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-3 rounded-lg ${colorConfig.bg}`}>
                    {pageConfig.icon}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      {pageConfig.title}
                    </h2>
                    <p className="text-gray-500 mt-1">
                      {pageConfig.description}
                    </p>
                  </div>
                </div>
                
                {/* 全局统计 */}
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <div className="text-lg font-bold text-gray-900">
                      {stats.onlineProviders}/{stats.totalProviders}
                    </div>
                    <div className="text-xs text-gray-500">在线供应商</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-gray-900">
                      {stats.activeSessions}
                    </div>
                    <div className="text-xs text-gray-500">活跃会话</div>
                  </div>
                  <div className={`px-3 py-2 rounded-lg ${colorConfig.bg} ${colorConfig.text} font-medium`}>
                    {pageConfig.title.includes('智能') ? '🤖 智能模式' : '🛠️ 管理模式'}
                  </div>
                </div>
              </div>
              
              {/* 进度指示器 */}
              <div className="mt-6">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium text-gray-700">功能流程</div>
                  <div className="text-xs text-gray-500">
                    步骤 {['config', 'providers', 'chat', 'history'].indexOf(activeTab) + 1}/4
                  </div>
                </div>
                <div className="flex items-center">
                  {(['config', 'providers', 'chat', 'history'] as const).map((tab, index) => {
                    const isActive = activeTab === tab;
                    const isCompleted = ['config', 'providers', 'chat', 'history'].indexOf(activeTab) > index;
                    const config = PAGE_CONFIG[tab];
                    const tabColor = COLOR_CLASSES[config.color as keyof typeof COLOR_CLASSES];
                    
                    return (
                      <React.Fragment key={tab}>
                        <div className="flex items-center">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            isActive || isCompleted
                              ? `${tabColor.bg} ${tabColor.text}`
                              : 'bg-gray-100 text-gray-400'
                          }`}>
                            {isCompleted ? '✓' : config.icon}
                          </div>
                          <div className="ml-2">
                            <div className={`text-sm font-medium ${
                              isActive || isCompleted ? 'text-gray-900' : 'text-gray-400'
                            }`}>
                              {config.title}
                            </div>
                          </div>
                        </div>
                        {index < 3 && (
                          <div className={`flex-1 h-1 mx-4 ${
                            isCompleted ? tabColor.bg : 'bg-gray-200'
                          }`} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* 当前供应商和会话信息 */}
            {(currentProvider || currentSession) && (
              <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                {currentProvider && (
                  <div className="card">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary-100 rounded-lg">
                          <Server className="w-5 h-5 text-primary-600" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">当前供应商</div>
                          <div className="text-sm text-gray-500">{currentProvider.name}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded">
                          {currentProvider.models.length} 个模型
                        </div>
                        <Activity className="w-4 h-4 text-green-500" />
                      </div>
                    </div>
                  </div>
                )}
                
                {currentSession && (
                  <div className="card">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-accent-100 rounded-lg">
                          <Cpu className="w-5 h-5 text-accent-600" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">当前会话</div>
                          <div className="text-sm text-gray-500">{currentSession.name}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">
                          {currentSession.messageCount} 条消息
                        </div>
                        <Activity className="w-4 h-4 text-blue-500" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 页面内容 */}
            <ContentComponent />
          </div>
        </main>
      </div>

      {/* 底部状态栏 */}
      <div className="border-t border-gray-200 bg-white px-6 py-3">
        <div className="flex items-center justify-between text-sm text-gray-500">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <span>系统运行正常</span>
            </div>
            <div>供应商: {stats.onlineProviders} 在线 / {stats.totalProviders} 总数</div>
            <div>会话: {stats.activeSessions} 活跃 / {stats.totalSessions} 总数</div>
          </div>
          <div>
            API 对话管理系统 v2.0 • 增强版
          </div>
        </div>
      </div>
    </div>
  );
};